import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import axios from '../../../api/axiosInstance';

const emptyRole = (role, catalogCategories = []) => ({
  role,
  label: role,
  description: '',
  categories: catalogCategories.map((category) => ({
    key: category.key,
    label: category.label,
    enabled: false,
  })),
});

const getEnabledCount = (categories = []) => categories.filter((item) => item.enabled).length;

export default function PermissionsPage({ embedded = false } = {}) {
  const [roles, setRoles] = useState([]);
  const [catalogCategories, setCatalogCategories] = useState([]);
  const [selectedRole, setSelectedRole] = useState('admin');
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newRole, setNewRole] = useState('');

  const currentRole = useMemo(
    () => roles.find((role) => role.role === selectedRole) || roles[0] || null,
    [roles, selectedRole]
  );

  const hydrateCategories = (role, categoriesCatalog = catalogCategories) => {
    const savedByKey = new Map((role?.categories || []).map((item) => [item.key, item]));
    return categoriesCatalog.map((category) => ({
      key: category.key,
      label: category.label,
      description: category.description,
      enabled: role?.role === 'admin' || Boolean(savedByKey.get(category.key)?.enabled),
    }));
  };

  const loadRoles = async (preferredRole = selectedRole) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/permissions/roles');
      const categories = data.catalog?.categories || [];
      const loadedRoles = (data.roles || []).map((role) => ({
        ...role,
        categories: hydrateCategories(role, categories),
      }));
      const nextRole = preferredRole && loadedRoles.some((role) => role.role === preferredRole)
        ? preferredRole
        : loadedRoles[0]?.role || 'admin';

      setCatalogCategories(categories);
      setRoles(loadedRoles);
      setSelectedRole(nextRole);
      setDraft(loadedRoles.find((role) => role.role === nextRole) || loadedRoles[0] || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de charger les rôles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentRole) {
      setDraft(JSON.parse(JSON.stringify(currentRole)));
    }
  }, [currentRole]);

  const updateCategory = (key, enabled) => {
    setDraft((current) => ({
      ...current,
      categories: (current?.categories || []).map((item) => item.key === key ? { ...item, enabled } : item),
    }));
  };

  const createRole = async () => {
    const role = newRole.trim().toLowerCase();
    if (!role) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = emptyRole(role, catalogCategories);
      await axios.put(`/api/permissions/roles/${role}`, payload);
      setNewRole('');
      setSuccess('Rôle créé.');
      await loadRoles(role);
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de créer le rôle.');
    } finally {
      setSaving(false);
    }
  };

  const saveRole = async () => {
    if (!draft) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(`/api/permissions/roles/${draft.role}`, {
        role: draft.role,
        label: draft.label || draft.role,
        description: draft.description || '',
        categories: draft.categories || [],
      });
      setSuccess('Rôle sauvegardé.');
      await loadRoles(draft.role);
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible de sauvegarder le rôle.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={embedded ? {} : { p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Stack spacing={2.5}>
        {!embedded && (
          <Box>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
              Administration
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Rôles
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Tabs
              value={selectedRole}
              onChange={(_, value) => setSelectedRole(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {roles.map((role) => (
                <Tab
                  key={role.role}
                  value={role.role}
                  label={role.role === 'admin' ? `${role.label || role.role} · admin` : role.label || role.role}
                />
              ))}
            </Tabs>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField
                label="Nouveau rôle"
                value={newRole}
                onChange={(event) => setNewRole(event.target.value)}
                size="small"
                sx={{ maxWidth: 280 }}
              />
              <Button variant="outlined" onClick={createRole} disabled={saving || !newRole.trim()}>
                Créer
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {draft && (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField label="Clé" value={draft.role} size="small" disabled />
                <TextField
                  label="Nom du rôle"
                  value={draft.label || ''}
                  size="small"
                  onChange={(event) => setDraft((current) => ({ ...current, label: event.target.value }))}
                  sx={{ minWidth: { md: 260 } }}
                  disabled={draft.role === 'admin'}
                />
                <Chip label={`${getEnabledCount(draft.categories)} catégorie(s)`} sx={{ width: 'fit-content' }} />
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" onClick={saveRole} disabled={saving || draft.role === 'admin'}>
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </Stack>

              {draft.role === 'admin' && (
                <Alert severity="info">Admin garde tous les accès.</Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                {(draft.categories || []).map((category) => (
                  <Paper
                    key={category.key}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: category.enabled ? 'primary.main' : 'divider',
                      bgcolor: category.enabled ? 'action.selected' : 'background.paper',
                    }}
                  >
                    <Stack spacing={1.25}>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            checked={draft.role === 'admin' || Boolean(category.enabled)}
                            disabled={draft.role === 'admin'}
                            onChange={(event) => updateCategory(category.key, event.target.checked)}
                          />
                        )}
                        label={(
                          <Typography sx={{ fontWeight: 800 }}>
                            {category.label || category.key}
                          </Typography>
                        )}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {category.description}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Box>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
